import { decamelizeKeys } from "humps";
import { isRole } from "PFApp/activities/helpers";
import { TemplateKey } from "PFApp/constants/templates";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useActivityVacancies } from "PFCore/hooks/queries/activity";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { Activity, AuditRole, ClosureReasonKey, Engagement, Option, Role, Snakify } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  buildCloseOptions,
  buildCloseReasons,
  findActivityTemplate,
  getActivityType,
  getIsSubmitDisabled
} from "./close_confirm_modal.utils";
import { useCloseActivityAction } from "./use_close_activity_action";

type UseCloseConfirmModalProps = {
  activity: Activity | AuditRole | Role | Engagement;
  type: TemplateKey;
  handleSuccess: VoidFunction;
};

export const useCloseConfirmModal = ({
  activity: activityData,
  type,
  handleSuccess
}: UseCloseConfirmModalProps) => {
  const { t } = useTranslation("activities");
  const { data: myAccountInfo } = useMyAccountInfo();

  const [selectedReason, setSelectedReason] = useState<ClosureReasonKey | null>(null);
  const [deleteBookings, setDeleteBookings] = useState(false);

  // activityData might come from different endpoints, camelized or not (see SP-2511)
  const activity = decamelizeKeys(activityData) as Activity | Snakify<AuditRole>;

  const template = findActivityTemplate(myAccountInfo.templates, activity, type);
  const filledOrBooked = activity.workflow_state === "booked" || activity.workflow_state === "filled";
  const activityType = getActivityType(template, t);
  const isRoleOrAuditRole = isRole(template);
  const isAuditRole = template?.key === TemplateKey.AuditRole;

  const { data: vacancies } = useActivityVacancies({ activityId: activity.id });
  const { data: shortlists, isFetching: isShortlistsFetching } = useActivityShortlists(
    activity.id,
    { source: "close_modal" }, // to not trigger refreshing shortlists list when opening the modal
    {
      enabled: !!activity.id && filledOrBooked && isAuditRole
    }
  );

  const closeOptions = useMemo<Option<ClosureReasonKey>[]>(
    () => buildCloseOptions(vacancies?.meta.availableClosureReasons || []),
    [vacancies?.meta]
  );

  const closeReasons = useMemo(
    () => buildCloseReasons(vacancies?.meta.availableClosureReasons || []),
    [vacancies?.meta]
  );

  const { closeVacanciesAndActivity } = useCloseActivityAction({
    activityId: activity.id,
    isAuditRole,
    selectedReason,
    deleteBookings,
    shortlistIds: (shortlists?.entries || []).map((shortlist) => shortlist.id),
    handleSuccess
  });

  const openVacancies = vacancies?.meta.totalOpened ?? 0;
  const hasOpenVacancies = isRoleOrAuditRole && !!vacancies && openVacancies > 0;
  const selectedCloseReason = selectedReason && closeReasons[selectedReason as keyof typeof closeReasons];
  const isSubmitDisabled = getIsSubmitDisabled({
    isAuditRole,
    hasOpenVacancies,
    selectedCloseReason,
    isShortlistsFetching
  });

  return {
    activity,
    template,
    activityType,
    isRoleOrAuditRole,
    isAuditRole,
    filledOrBooked,
    vacancies,
    shortlists,
    isShortlistsFetching,
    closeOptions,
    selectedReason,
    setSelectedReason,
    deleteBookings,
    setDeleteBookings,
    closeVacanciesAndActivity,
    openVacancies,
    hasOpenVacancies,
    isSubmitDisabled
  };
};
